import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    WritableSignal
} from '@angular/core';
import { AvatarBaseDirective, AvatarDirective } from '@siemens/component-lib';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../auth/services/auth.service';
import { GraphService } from '../../../auth/services/graph.service';
import { environment } from '../../../../environments/environment';
import { takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-user-profile',
    imports: [AvatarDirective, AvatarBaseDirective, TranslatePipe],
    templateUrl: './user-profile.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileComponent implements OnInit, OnDestroy {
    public readonly userId = input<string | undefined>();
    public readonly showData = input<boolean>(false);

    protected authService = inject(AuthService);
    protected graphService = inject(GraphService);

    private _userPhoto: WritableSignal<Blob | null> = signal<Blob | null>(null);
    private _userName: WritableSignal<string | null> = signal<string | null>(null);
    private _userOrgCode: WritableSignal<string | null> = signal<string | null>(null);
    private destroy$: Subject<boolean> = new Subject<boolean>();

    ngOnInit(): void {
        this.getUserDetails();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private getUserDetails(): void {
        const userId = this.userId();
        if (userId) {
            this.graphService
                .getUserDetails(userId)
                .pipe(
                    tap(userDetails => {
                        this._userName.set(`${userDetails.value[0].givenName} ${userDetails.value[0].surname}`);
                        this._userOrgCode.set(userDetails.value[0].displayName);
                    }),
                    takeUntil(this.destroy$)
                )
                .subscribe();

            const scopes = environment.apiConfig[0].scopes;
            this.graphService
                .getPhotoForUser(userId, scopes)
                .pipe(
                    tap(photo => {
                        this._userPhoto.set(photo);
                    }),
                    takeUntil(this.destroy$)
                )
                .subscribe();
        }
    }

    public getUserPhotoUrl(): string | null {
        if (!this.userId()) {
            return this.authService.getUserPhotoUrl();
        }

        const photo = this._userPhoto();
        return photo ? URL.createObjectURL(photo) : null;
    }

    public getUserName(): string | null {
        if (!this.userId()) {
            return this.authService.userName();
        }

        return this._userName();
    }

    public getUserOrgCode(): string | null {
        if (!this.userId()) {
            return this.authService.userIdTokenClaims()?.org_code || null;
        }

        const orgCode = this._userOrgCode() ?? '';
        return this.extractOrgCode(orgCode);
    }

    private extractOrgCode(input: string): string | null {
        const match = input.match(/\(([^)]+)\)/);
        return match ? match[1] : null;
    }
}
